
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

export const useAuthHandlers = () => {
  const [loading, setLoading] = useState(false);
  const { signIn, signUp } = useAuth();

  const handleLogin = async (email: string, password: string) => {
    console.log("Attempting login for:", email);
    const { error } = await signIn(email, password);
    if (error) {
      console.error("Login error:", error);
      if (error.message.includes("Invalid login credentials")) {
        throw new Error("Invalid email or password. Please check your credentials and try again.");
      } else {
        throw new Error(error.message);
      }
    }
  };

  const handleSignup = async (email: string, password: string, firstName: string, lastName: string, favoriteProviderId?: string | null) => {
    const { error } = await signUp(email, password, firstName, lastName, favoriteProviderId);
    if (error) {
      if (error.message.includes("User already registered")) {
        throw new Error("An account with this email already exists. Try logging in instead.");
      } else {
        throw new Error(error.message);
      }
    }
    return "Check your email for the confirmation link to complete your registration!";
  };

  const handlePasswordReset = async (newPassword: string, confirmPassword: string) => {
    if (newPassword !== confirmPassword) {
      throw new Error("Passwords do not match.");
    }
    if (newPassword.length < 6) {
      throw new Error("Password must be at least 6 characters long.");
    }

    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      throw new Error(error.message);
    }
    // Don't return a message since we'll redirect to dashboard
  };

  const handleForgotPassword = async (email: string) => {
    if (!email) {
      throw new Error("Please enter your email address first.");
    }

    // Force production URL - no dynamic detection
    const redirectTo = "https://wellsuites.co/auth";
    
    console.log("=== PASSWORD RESET DEBUG ===");
    console.log("Sending password reset email to:", email);
    console.log("FORCED redirect URL:", redirectTo);
    console.log("Current window location:", window.location.href);
    console.log("Current hostname:", window.location.hostname);
    console.log("Current origin:", window.location.origin);
    console.log("Timestamp:", new Date().toISOString());
    
    // Log the exact payload being sent to Supabase
    const resetPayload = {
      redirectTo: redirectTo
    };
    console.log("Exact payload being sent to Supabase:", JSON.stringify(resetPayload, null, 2));
    console.log("========================");

    const { error } = await supabase.auth.resetPasswordForEmail(email, resetPayload);

    if (error) {
      console.error("Password reset error:", error);
      throw new Error(error.message);
    }
    
    console.log("Password reset email sent successfully to:", email);
    console.log("Expected redirect URL:", redirectTo);
    console.log("If email still shows wrong URL, check Supabase Auth settings");
    return "Password reset email sent! Check your email for the reset link.";
  };

  const handleResendConfirmation = async (email: string) => {
    if (!email) {
      throw new Error("Please enter your email address first.");
    }

    const redirectTo = "https://wellsuites.co/auth";
    
    console.log("Resending confirmation email to:", email);
    
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email,
      options: {
        emailRedirectTo: redirectTo
      }
    });

    if (error) {
      console.error("Resend confirmation error:", error);
      throw new Error(error.message);
    }
    
    console.log("Confirmation email resent successfully to:", email);
    return "Confirmation email sent! Check your email for the verification link.";
  };

  return {
    loading,
    setLoading,
    handleLogin,
    handleSignup,
    handlePasswordReset,
    handleForgotPassword,
    handleResendConfirmation
  };
};
